import React, { useCallback, useEffect, useRef, useState } from 'react';
import { columnType, dataType, Order, OrderBy, tableState, initialTableState, IServerPaginationConfig } from './types';
import { BaseTable } from './BaseTable';
import ServerTablePagination from './sections/ServerTablePagination';
import { BasicTableStyle } from './style';

export interface IServerPaginatedTableProps {
	data: dataType[];
	columns: columnType[];
	serverPagination: IServerPaginationConfig;
	rowAction?: (row: dataType) => void;
	rowsClass?: string;
	tableClass?: string;
	defaultSortBy?: string;
	defaultOrderBy?: Order;
	hideHeader?: boolean;
	noShrink?: boolean;
	dateFormat?: string;
	perPage?: number;
	getRowStyle?: (row: dataType) => React.CSSProperties | undefined;
}

export const ServerPaginatedTable: React.FC<IServerPaginatedTableProps> = ({
	data,
	columns,
	serverPagination,
	rowAction,
	rowsClass,
	tableClass,
	defaultSortBy,
	defaultOrderBy,
	hideHeader,
	noShrink,
	dateFormat,
	perPage = 10,
	getRowStyle,
}) => {
	const [internalLoading, setInternalLoading] = useState(false);
	const isInitialSort = useRef(true);
	const lastSortRef = useRef<{ sortBy?: string; orderBy?: Order } | null>(null);
	const [tableState, setTableState] = useState<tableState>({
		...initialTableState,
		sortBy: defaultSortBy || '',
		orderBy: defaultOrderBy || (OrderBy.DESENDING as Order),
		perPage: perPage,
	});

	const handlePageChange = useCallback(
		async (page: number) => {
			if (!serverPagination?.onPageChange) return;
			setInternalLoading(true);
			try {
				await serverPagination.onPageChange(page);
			} finally {
				setInternalLoading(false);
			}
		},
		[serverPagination]
	);

	const isLoading = serverPagination?.isLoading ?? internalLoading;

	useEffect(() => {
		if (!serverPagination?.onSortChange) return;

		if (isInitialSort.current) {
			isInitialSort.current = false;
			lastSortRef.current = { sortBy: tableState.sortBy, orderBy: tableState.orderBy };
			return;
		}

		if (!tableState.sortBy) return;

		const currentSort = { sortBy: tableState.sortBy, orderBy: tableState.orderBy };
		if (
			lastSortRef.current?.sortBy === currentSort.sortBy &&
			lastSortRef.current?.orderBy === currentSort.orderBy
		) {
			return;
		}

		lastSortRef.current = currentSort;
		setInternalLoading(true);
		Promise.resolve(serverPagination.onSortChange(currentSort.sortBy, currentSort.orderBy)).finally(() => {
			setInternalLoading(false);
		});
	}, [serverPagination, tableState.orderBy, tableState.sortBy]);

	return (
		<BasicTableStyle spacing={3}>
			<BaseTable
				data={data}
				columns={columns}
				rowAction={rowAction}
				rowsClass={rowsClass}
				tableClass={tableClass}
				defaultSortBy={defaultSortBy}
				defaultOrderBy={defaultOrderBy}
				hideHeader={hideHeader}
				noShrink={noShrink}
				dateFormat={dateFormat}
				isLoading={isLoading}
				tableState={tableState}
				setTableState={setTableState}
				getRowStyle={getRowStyle}
			/>
			<ServerTablePagination
				config={serverPagination}
				perPage={perPage}
				onPageChange={handlePageChange}
				isLoading={isLoading}
			/>
		</BasicTableStyle>
	);
};

export default ServerPaginatedTable;
